import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Tag, Typography, Button, Input, Select, Space,
  Empty, Spin, Tooltip, Drawer, Alert, message, Popover, Checkbox,
} from 'antd';
import axios from 'axios';
import RevisionUploadForm from './RevisionUploadForm';

const { Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ── neutral greys + faint sky-blue accent (+ amber for ON HOLD) ── */
const T = {
  ink: '#1f1f1f', sub: '#595959', muted: '#8c8c8c', faint: '#bfbfbf',
  border: '#d9d9d9', line: '#ececec', hairline: '#f0f0f0',
  panel: '#fafafa', head: '#f5f5f5', white: '#ffffff',
  // faint sky-blue accent (replaces the old black/ink accents)
  blue: '#3f74ad', blueHover: '#4f86c6', blueActive: '#356296',
  blueSoft: '#eaf3fc', blueLine: '#d6e6f5',
  // ON HOLD accent
  hold: '#d46b08', holdSoft: '#fff7e6', holdLine: '#ffd591',
};
const primaryBtn = { background: T.blue, borderColor: T.blue, color: '#fff' };

/* Spreadsheet grid — bordered, tight, single-line cells, no row-gap */
const GRID_CSS = `
  .pts-log .ant-table { font-size: 12px; }
  .pts-log .ant-table-cell {
    padding: 6px 10px !important;
    border-color: ${T.line} !important;
    white-space: nowrap;
  }
  .pts-log .ant-table-thead > tr > th {
    background: ${T.head} !important;
    color: ${T.sub} !important;
    font-weight: 600 !important;
    border-color: ${T.line} !important;
    padding: 7px 10px !important;
  }
  .pts-log .ant-table-tbody > tr > td { color: ${T.ink}; }
  .pts-log .ant-table-tbody > tr:hover > td { background: ${T.blueSoft} !important; }
  .pts-log .ant-table-row-rejected > td { background: #fbfbfb; }
  /* ON HOLD rows — faint amber with a left marker */
  .pts-log .ant-table-row-hold > td { background: #fffdf5; }
  .pts-log .ant-table-row-hold:hover > td { background: ${T.holdSoft} !important; }
  .pts-log .ant-table-row-hold > td:first-child { box-shadow: inset 3px 0 0 ${T.hold}; }
`;

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai' });
};

const formatDateTime = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleString('en-GB', {
    timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const CATEGORY_LABELS = {
  material: 'Materials', labor_supply: 'Labor Supply', sub_contractor: 'Sub-Contractors',
  heavy_equipment_rental: 'Heavy Equip.', equipment_rental: 'Equipment',
  fees_and_charges: 'Fees & Charges', repairs_and_maintenance: 'Repairs & Maint.',
};

const STATUS_LABELS = {
  pending_review: 'Pending Review',
  under_review: 'Under Review',
  on_hold: 'On Hold',
  approved: 'Approved',
  rejected: 'Rejected',
};

/* Plain neutral status cell — rejected keeps the blue accent, on hold gets amber */
const StatusCell = ({ status }) => {
  const label = STATUS_LABELS[status] || status || '-';
  const isRejected = status === 'rejected';
  const isHold = status === 'on_hold';
  const borderColor = isHold ? T.holdLine : (isRejected ? T.blue : T.border);
  const textColor = isHold ? T.hold : (isRejected ? T.blue : T.ink);
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 2,
      border: `1px solid ${borderColor}`,
      background: isHold ? T.holdSoft : 'transparent',
      fontSize: 11, color: textColor,
      fontWeight: (isRejected || isHold) ? 600 : 500, lineHeight: '16px',
    }}>{label}</span>
  );
};

const LogSheetView = ({ username, isAccounts, viewAll, onViewDetail }) => {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [batchNos, setBatchNos] = useState([]);

  // How many of MY invoices Accounts is currently holding (drives the banner)
  const [myHoldCount, setMyHoldCount] = useState(0);

  // Revision drawer
  const [revisionDrawerOpen, setRevisionDrawerOpen] = useState(false);
  const [revisionSubmission, setRevisionSubmission] = useState(null);

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Column visibility — TRN and DN No. hidden by default; user can toggle from the Columns chooser
  const [hiddenKeys, setHiddenKeys] = useState(['trn', 'dn']);

  /* Debounce search input → drives the server query */
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchText.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, offset: (page - 1) * pageSize };
      if (!viewAll) params.submitted_by = username;
      if (selectedStatus !== 'all') params.status = selectedStatus;
      if (selectedBatch !== 'all') params.batch_no = selectedBatch;
      // Server-side search across ref / vendor / supplier / LPO / invoice — searches ALL records, not just the loaded page
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, { params });
      setSubmissions(res.data.submissions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [username, isAccounts, viewAll, page, pageSize, selectedStatus, selectedBatch, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Hold count — independent of the current page / filters */
  const fetchHoldCount = useCallback(async () => {
    try {
      const params = {};
      if (!viewAll) params.submitted_by = username;
      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions/on-hold`, { params });
      setMyHoldCount(res.data.count || 0);
    } catch {
      setMyHoldCount(0);
    }
  }, [username, viewAll]);

  useEffect(() => { fetchHoldCount(); }, [fetchHoldCount]);

  /* Load the full batch list once for the filter (independent of current page) */
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/procurement/batches`);
        if (res.data.success) {
          const nums = (res.data.batches || []).map(b => b.batch_no).filter(Boolean).sort((a, b) => a - b);
          if (nums.length) setBatchNos(nums);
        }
      } catch { /* fall back to deriving from page below */ }
    })();
  }, []);

  // Fallback: if the batch endpoint gave nothing, derive from current page
  const batchOptions = batchNos.length
    ? batchNos
    : [...new Set(submissions.map(s => s.batch_no).filter(Boolean))].sort((a, b) => a - b);

  const openRevisionDrawer = (record) => {
    setRevisionSubmission(record);
    setRevisionDrawerOpen(true);
  };

  const downloadBlob = (res, fallbackName, mime, headerKey) => {
    let filename = fallbackName;
    const disposition = res.headers['content-disposition'];
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }
    const custom = res.headers[headerKey];
    if (custom) filename = custom;
    const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
    const link = document.createElement('a');
    link.href = url; link.download = filename;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    message.success(`Downloaded ${filename}`);
  };

  const handleDownloadBatchPdf = async () => {
    if (selectedBatch === 'all') { message.warning('Select a specific batch to download the PDF report'); return; }
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${selectedBatch}/report/pdf`, { responseType: 'blob', timeout: 60000 });
      downloadBlob(res, `${selectedBatch}-report.pdf`, 'application/pdf', 'x-pdf-filename');
    } catch (err) {
      if (err.response?.status === 404) message.warning(`No invoices found in Batch ${selectedBatch}`);
      else message.error('Failed to download batch report');
    } finally { setDownloadingPdf(false); }
  };

  const handleDownloadBatchExcel = async () => {
    if (selectedBatch === 'all') { message.warning('Select a specific batch to download the Excel report'); return; }
    setDownloadingExcel(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${selectedBatch}/report/excel`, { responseType: 'blob', timeout: 60000 });
      downloadBlob(res, `Batch-${selectedBatch}-Invoices-Report.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'x-excel-filename');
    } catch (err) {
      if (err.response?.status === 404) message.warning(`No invoices found in Batch ${selectedBatch}`);
      else message.error('Failed to download Excel report');
    } finally { setDownloadingExcel(false); }
  };

  // Server now handles search; render rows as returned
  const data = submissions;

  // Held invoices on the page currently being viewed
  const heldOnPage = useMemo(() => data.filter(s => s.status === 'on_hold'), [data]);

  const cell = (t, opts = {}) => (
    <span style={{ fontSize: 12, color: opts.muted ? T.muted : T.ink, fontWeight: opts.strong ? 600 : 400 }}>{t || '—'}</span>
  );

  const columns = [
    {
      title: 'Batch', dataIndex: 'batch_no', key: 'batch', width: 64, fixed: 'left',
      sorter: (a, b) => (a.batch_no || 0) - (b.batch_no || 0),
      render: (v) => v ? <Text strong style={{ fontSize: 12, color: T.ink }}>{v}</Text> : '—',
    },
    {
      title: 'Category', dataIndex: 'category', key: 'cat', width: 120, fixed: 'left',
      render: (c) => <Text strong style={{ fontSize: 12, color: T.blue }}>{CATEGORY_LABELS[c] || c || '—'}</Text>,
    },
    { title: 'LPO', dataIndex: 'lpo_number', key: 'lpo', width: 78, render: (t) => cell(t, { strong: true }) },
    {
      title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor', width: 190,
      render: (t) => <Tooltip title={t}><span style={{ fontSize: 12, color: t ? T.ink : T.faint, display: 'inline-block', maxWidth: 178, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{t || '—'}</span></Tooltip>,
    },
    { title: 'Invoice No.', dataIndex: 'invoice_number', key: 'inv', width: 120, render: (t) => cell(t) },
    { title: 'Inv. Date', dataIndex: 'invoice_date', key: 'inv_date', width: 96, render: (t) => cell(t, { muted: true }) },
    {
      title: 'Total', dataIndex: 'total_amount', key: 'amount', width: 128, align: 'right',
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
      render: (val) => <Text strong style={{ fontSize: 12, color: T.ink }}>{formatAmount(val)}</Text>,
    },
    { title: 'VAT', dataIndex: 'vat_amount', key: 'vat', width: 104, align: 'right', render: (val) => <span style={{ fontSize: 11, color: T.sub }}>{formatAmount(val)}</span> },
    { title: 'TRN', dataIndex: 'vendor_trn', key: 'trn', width: 130, render: (t) => <span style={{ fontSize: 11, color: t ? T.sub : T.faint }}>{t || '—'}</span> },
    { title: 'DN No.', dataIndex: 'dn_number', key: 'dn', width: 84, render: (t) => cell(t, { muted: true }) },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => <StatusCell status={s} /> },
    { title: 'Rev', dataIndex: 'revision_count', key: 'rev', width: 48, align: 'center', render: (v) => v > 0 ? <Text strong style={{ fontSize: 11, color: T.ink }}>#{v}</Text> : '—' },
    {
      title: 'Submitted', dataIndex: 'submitted_at', key: 'at', width: 118,
      render: (v) => (
        <span style={{ fontSize: 11, color: T.sub }}>
          {formatDate(v)} {v ? new Date(v).toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: false }) : ''}
        </span>
      ),
    },
    { title: 'By', dataIndex: 'submitted_by', key: 'user', width: 110, render: (t) => <span style={{ fontSize: 11, color: T.sub }}>{t ? t.split('@')[0] : '—'}</span> },
    {
      // Carries the accounts decision AND the auto-generated hold note
      title: 'Remarks', dataIndex: 'accounts_remarks', key: 'remarks', width: 210,
      render: (t, record) => {
        const held = record.status === 'on_hold';
        const text = held ? (record.hold_remarks || t) : t;
        if (!text) return '—';
        return (
          <Tooltip title={text}>
            <span style={{
              fontSize: 11, color: held ? T.hold : T.sub, fontWeight: held ? 600 : 400,
              display: 'inline-block', maxWidth: 198, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle',
            }}>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action', key: 'action', width: 130, fixed: 'right',
      render: (_, record) => (
        <Space size={10}>
          <span style={{ color: T.blue, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }} onClick={() => onViewDetail && onViewDetail(record.id)}>View</span>
          {record.status === 'rejected' && !isAccounts && (
            <span style={{ color: T.blue, cursor: 'pointer', fontSize: 12, textDecoration: 'underline', fontWeight: 600 }} onClick={() => openRevisionDrawer(record)}>Revise</span>
          )}
          {record.status === 'on_hold' && (
            <Tooltip title="Accounts is reviewing this invoice. Nothing to do from your side until the hold is released.">
              <span style={{ color: T.hold, fontSize: 11, fontWeight: 600 }}>With accounts</span>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Columns the user can show/hide (Batch, Category and Action stay fixed)
  const TOGGLE_COLS = [
    { key: 'lpo', label: 'LPO' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'inv', label: 'Invoice No.' },
    { key: 'inv_date', label: 'Inv. Date' },
    { key: 'amount', label: 'Total' },
    { key: 'vat', label: 'VAT' },
    { key: 'trn', label: 'TRN' },
    { key: 'dn', label: 'DN No.' },
    { key: 'status', label: 'Status' },
    { key: 'rev', label: 'Rev' },
    { key: 'at', label: 'Submitted' },
    { key: 'user', label: 'By' },
    { key: 'remarks', label: 'Remarks' },
  ];
  const checkedCols = TOGGLE_COLS.map(c => c.key).filter(k => !hiddenKeys.includes(k));
  const onColsChange = (vals) => setHiddenKeys(TOGGLE_COLS.map(c => c.key).filter(k => !vals.includes(k)));
  const visibleColumns = columns.filter(c => !hiddenKeys.includes(c.key));

  const columnsChooser = (
    <div style={{ minWidth: 150 }}>
      <Checkbox.Group value={checkedCols} onChange={onColsChange}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {TOGGLE_COLS.map(c => <Checkbox key={c.key} value={c.key}>{c.label}</Checkbox>)}
        </div>
      </Checkbox.Group>
    </div>
  );

  return (
    <div className="pts-log">
      <style>{GRID_CSS}</style>

      {/* ── ON HOLD banner — informational only, no action needed ── */}
      {myHoldCount > 0 && selectedStatus !== 'on_hold' && (
        <Alert
          type="warning" showIcon
          message={`${myHoldCount} invoice${myHoldCount > 1 ? 's are' : ' is'} on hold with Accounts`}
          description="Accounts has parked these while they review them. You do not need to re-upload or revise anything — the note against each invoice names the supplier and invoice number."
          action={
            <Button size="small" onClick={() => { setSelectedStatus('on_hold'); setPage(1); }} style={{ borderRadius: 2 }}>
              Show them
            </Button>
          }
          style={{ marginBottom: 14, borderRadius: 4 }}
        />
      )}

      <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white }}>
        {/* Header bar */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.hairline}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <Text strong style={{ fontSize: 14, color: T.ink }}>Invoice Log Sheet</Text>
            <Text style={{ fontSize: 12, color: T.muted, marginLeft: 10 }}>{total} records</Text>
            {myHoldCount > 0 && (
              <Text style={{ fontSize: 12, color: T.hold, marginLeft: 10, fontWeight: 600 }}>
                · {myHoldCount} on hold
              </Text>
            )}
          </div>
          <Space size={8}>
            <Popover content={columnsChooser} trigger="click" placement="bottomRight" title={<span style={{ fontSize: 12, color: T.sub }}>Show columns</span>}>
              <Button style={{ borderRadius: 2 }}>Columns</Button>
            </Popover>
            <Tooltip title={selectedBatch === 'all' ? 'Select a batch first' : `Download Batch ${selectedBatch} PDF`}>
              <Button onClick={handleDownloadBatchPdf} loading={downloadingPdf} disabled={selectedBatch === 'all'} style={{ borderRadius: 2 }}>PDF</Button>
            </Tooltip>
            <Tooltip title={selectedBatch === 'all' ? 'Select a batch first' : `Download Batch ${selectedBatch} Excel`}>
              <Button onClick={handleDownloadBatchExcel} loading={downloadingExcel} disabled={selectedBatch === 'all'} style={{ borderRadius: 2 }}>Excel</Button>
            </Tooltip>
            <Button onClick={() => { fetchData(); fetchHoldCount(); }} loading={loading} style={{ borderRadius: 2 }}>Refresh</Button>
          </Space>
        </div>

        {/* Filters */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.hairline}`, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input placeholder="Search ref, supplier, vendor, LPO, invoice…"
            value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear
            style={{ borderRadius: 2, maxWidth: 300, height: 32, fontSize: 12 }} />
          <Select value={selectedStatus} onChange={(val) => { setSelectedStatus(val); setPage(1); }}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'pending_review', label: 'Pending Review' },
              { value: 'on_hold', label: 'On Hold' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ]} />
          <Select value={selectedBatch} onChange={(val) => { setSelectedBatch(val); setPage(1); }}
            style={{ width: 150 }} showSearch
            filterOption={(input, option) => String(option.label).includes(input)}
            options={[{ value: 'all', label: 'All Batches' }, ...batchOptions.map(b => ({ value: b, label: `Batch ${b}` }))]} />
          {debouncedSearch && <Text style={{ fontSize: 12, color: T.muted }}>Searching all records for “{debouncedSearch}”</Text>}
          {selectedStatus === 'on_hold' && (
            <Text style={{ fontSize: 12, color: T.hold }}>
              Held by Accounts — the Remarks column carries the reason
            </Text>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 70, textAlign: 'center' }}><Spin /></div>
        ) : data.length === 0 ? (
          <Empty style={{ padding: 56 }} description={<Text type="secondary">No submissions found</Text>} />
        ) : (
          <Table
            columns={visibleColumns}
            dataSource={data.map(s => ({ ...s, key: s.id }))}
            pagination={{
              current: page, pageSize, total,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
              showSizeChanger: true, size: 'small',
              showTotal: (t) => `${t} records`,
              pageSizeOptions: ['20', '50', '100'],
            }}
            size="small" bordered scroll={{ x: 1820 }}
            rowClassName={(record) =>
              record.status === 'on_hold' ? 'ant-table-row-hold'
                : record.status === 'rejected' ? 'ant-table-row-rejected' : ''}
            expandable={{
              // Held rows expand to show the full hold note without truncation
              expandedRowRender: (record) => (
                <div style={{ padding: '8px 12px', background: T.holdSoft, border: `1px solid ${T.holdLine}`, borderRadius: 4 }}>
                  <Text strong style={{ fontSize: 12, color: T.hold, display: 'block', marginBottom: 4 }}>
                    On hold with Accounts
                  </Text>
                  <Text style={{ fontSize: 12, color: T.ink, whiteSpace: 'normal' }}>
                    {record.hold_remarks || record.accounts_remarks}
                  </Text>
                  <Text style={{ fontSize: 11, color: T.muted, display: 'block', marginTop: 4 }}>
                    Held by {record.held_by ? record.held_by.split('@')[0] : 'accounts'} on {formatDateTime(record.held_at)}
                  </Text>
                </div>
              ),
              rowExpandable: (record) => record.status === 'on_hold',
              showExpandColumn: heldOnPage.length > 0,
            }}
          />
        )}
      </div>

      {/* Revision Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: T.ink }}>Submit Revised Invoice</span>
            {revisionSubmission && (
              <span style={{ fontSize: 12, color: T.sub, border: `1px solid ${T.border}`, borderRadius: 2, padding: '1px 8px' }}>
                {revisionSubmission.submission_ref}
              </span>
            )}
          </div>
        }
        placement="right" open={revisionDrawerOpen}
        onClose={() => { setRevisionDrawerOpen(false); setRevisionSubmission(null); }}
        width={Math.min(700, window.innerWidth * 0.9)}
        destroyOnClose
      >
        {revisionSubmission && (
          <div>
            <Alert
              type="warning" showIcon
              message="Re-submit under the same batch"
              description={`This rejected invoice belongs to Batch ${revisionSubmission.batch_no}. Upload the corrected document here so it stays in Batch ${revisionSubmission.batch_no} — do not re-submit it as a new invoice in a different batch.`}
              style={{ marginBottom: 16, borderRadius: 4 }}
            />

            {revisionSubmission.accounts_remarks && (
              <Alert type="error" showIcon message="Rejection reason"
                description={revisionSubmission.accounts_remarks}
                style={{ marginBottom: 16, borderRadius: 4 }} />
            )}

            <div style={{ marginBottom: 16, borderRadius: 4, border: `1px solid ${T.line}`, padding: '12px 14px', background: T.panel }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Batch</Text><Text strong>{revisionSubmission.batch_no}</Text></div>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>LPO</Text><Text strong>{revisionSubmission.lpo_number}</Text></div>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Vendor</Text><Text>{revisionSubmission.vendor_name || '-'}</Text></div>
                <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Amount</Text><Text strong>{formatAmount(revisionSubmission.total_amount)}</Text></div>
              </div>
            </div>

            <RevisionUploadForm
              submissionId={revisionSubmission.id}
              submissionRef={revisionSubmission.submission_ref}
              category={revisionSubmission.category}
              rejectionRemarks={revisionSubmission.accounts_remarks}
              username={username}
              onComplete={() => {
                setRevisionDrawerOpen(false);
                setRevisionSubmission(null);
                fetchData();
                fetchHoldCount();
              }}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LogSheetView;